// Desert vivarium — page chrome: header HUD, species index, species card, bottom bar.
import React, { useEffect, useState } from "react";
import { SPECIES } from "./data";

export function VivHeader({ day, onDay, onPhoto }) {
  const [m, setM] = useState({ temp: 96.2, rh: 22, uv: 8.1 });
  useEffect(() => {
    const id = setInterval(() => setM((p) => ({
      temp: +((day ? 96.2 : 71.5) + (Math.random() - 0.5) * 1.4).toFixed(1),
      rh: Math.round((day ? 22 : 34) + (Math.random() - 0.5) * 4),
      uv: day ? +(8.1 + (Math.random() - 0.5) * 0.5).toFixed(1) : 0,
    })), 2600);
    return () => clearInterval(id);
  }, [day]);
  return (
    <header className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] bg-stone-950/90 backdrop-blur-xl border-b border-amber-500/20" data-testid="vivarium-header">
      <div className="min-w-0">
        <h1 className="text-base sm:text-xl font-extrabold tracking-tight text-stone-100 truncate" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
          DESERT VIVARIUM <span className="text-amber-400">//</span> PALUDARIUM
        </h1>
        <p className="text-[10px] sm:text-xs font-mono tracking-widest text-stone-400 uppercase">Full living ecosystem — flora, fauna, water</p>
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        {[["TEMP", `${m.temp}°F`, "viv-hud-temp"], ["RH", `${m.rh}%`, "viv-hud-rh"], ["UV", m.uv, "viv-hud-uv"]].map(([k, v, tid]) => (
          <div key={k} className="hidden sm:block px-3 py-1.5 rounded-lg bg-slate-900/70 border border-slate-700/50" data-testid={tid}>
            <div className="text-[9px] font-mono tracking-widest text-amber-400/90">{k}</div>
            <div className="text-sm font-mono text-stone-100">{v}</div>
          </div>
        ))}
        <button
          data-testid="viv-photo-toggle"
          onClick={onPhoto}
          className="px-3 sm:px-4 py-2 rounded-lg font-mono text-[11px] tracking-widest border transition-colors duration-200 min-h-[44px] bg-stone-950 text-amber-200 border-amber-500/40 hover:border-amber-300"
        >
          📷 PHOTOS
        </button>
        <button
          data-testid="viv-day-toggle"
          onClick={onDay}
          className={`px-3 sm:px-4 py-2 rounded-lg font-mono text-[11px] tracking-widest border transition-colors duration-200 min-h-[44px] ${
            day ? "bg-amber-400 text-stone-950 border-amber-300" : "bg-indigo-950 text-indigo-200 border-indigo-500/50"
          }`}
        >
          {day ? "☀ DAY" : "☾ NIGHT"}
        </button>
      </div>
    </header>
  );
}

export function SpeciesIndex({ selected, onSelect }) {
  return (
    <aside className="hidden md:flex flex-col gap-1.5 absolute left-4 top-4 bottom-4 w-64 p-3 rounded-xl bg-stone-950/85 backdrop-blur-xl border border-amber-500/20 shadow-2xl z-10 overflow-y-auto" data-testid="species-index">
      <div className="text-[10px] font-mono tracking-widest text-amber-400/90 uppercase px-1 pb-1 border-b border-slate-700/50">Ecosystem roster</div>
      {SPECIES.filter((s) => s.id !== "viv-datepalm2").map((s) => (
        <button
          key={s.id}
          data-testid={`species-item-${s.id}`}
          onClick={() => onSelect(s.id)}
          className={`text-left px-3 py-2 rounded-lg border transition-all duration-200 min-h-[44px] ${
            selected === s.id ? "bg-amber-400/10 border-amber-400/70" : "bg-slate-900/50 border-slate-700/50 hover:border-amber-500/40"
          }`}
        >
          <div className="text-xs font-semibold text-stone-100">{s.name}</div>
          <div className="text-[9px] font-mono italic text-stone-500">{s.sci}</div>
        </button>
      ))}
    </aside>
  );
}

export function SpeciesCard({ selected, onClose }) {
  const sp = SPECIES.find((x) => x.id === selected);
  if (!sp) {
    return (
      <aside className="hidden md:block absolute right-4 top-4 w-80 p-4 rounded-xl bg-stone-950/85 backdrop-blur-xl border border-slate-700/50 z-10" data-testid="species-card-empty">
        <div className="relative h-36 rounded-lg overflow-hidden border border-amber-500/20 mb-3">
          <img src="/assets/vivarium/viv-hero.png" alt="Photoreal render of the full desert vivarium ecosystem" className="w-full h-full object-cover growlab-kenburns" />
          <span className="absolute bottom-1.5 left-2 text-[8px] font-mono tracking-widest text-amber-300/80">THE LIVING TARGET · AI RENDER</span>
        </div>
        <p className="text-xs font-mono tracking-widest text-stone-400 leading-relaxed">TAP ANY PLANT OR ANIMAL TO READ ITS ROLE IN THE ECOSYSTEM.</p>
      </aside>
    );
  }
  return (
    <aside className="absolute z-20 left-2 right-2 bottom-2 top-auto max-h-[62%] overflow-y-auto sm:left-auto sm:right-4 sm:top-4 sm:bottom-auto sm:w-80 sm:max-h-none p-4 rounded-xl bg-stone-950/90 backdrop-blur-xl border border-amber-500/30 shadow-2xl" data-testid="species-card">
      <div className="relative h-28 sm:h-40 rounded-lg overflow-hidden border border-amber-500/25 mb-3" data-testid="species-photo">
        <img src={`/assets/vivarium/${sp.img || sp.id}.png`} alt={`Photoreal render of ${sp.name}`} className="w-full h-full object-cover growlab-kenburns" />
      </div>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[9px] font-mono tracking-widest text-emerald-400 uppercase">{sp.kind}</div>
          <h2 className="text-lg font-bold text-stone-100" style={{ fontFamily: "Georgia, serif" }}>{sp.name}</h2>
          <div className="text-[10px] font-mono italic text-stone-400">{sp.sci}</div>
        </div>
        <button data-testid="species-close-button" onClick={onClose} className="text-stone-400 hover:text-amber-300 font-mono text-sm px-2 min-h-[44px]">✕</button>
      </div>
      <p className="text-sm text-stone-300 leading-relaxed mt-3">{sp.role}</p>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {sp.metrics.map(([k, v]) => (
          <div key={k} className="px-2 py-1.5 rounded bg-slate-900/70 border border-slate-700/50">
            <div className="text-[8px] font-mono tracking-widest text-stone-500">{k}</div>
            <div className="text-[10px] font-mono text-stone-100">{v}</div>
          </div>
        ))}
      </div>
    </aside>
  );
}

export function VivBottomBar({ running, onRun }) {
  const btn = "px-3 py-2 rounded-lg font-mono text-[10px] tracking-widest border transition-colors duration-200 min-h-[44px]";
  return (
    <footer className="flex items-center gap-2 px-4 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-stone-950/90 backdrop-blur-xl border-t border-amber-500/20 overflow-x-auto" data-testid="vivarium-bottom-bar">
      <button data-testid="viv-play-toggle" onClick={onRun} className={`${btn} ${running ? "bg-amber-400 text-stone-950 border-amber-300" : "bg-stone-950 text-stone-300 border-slate-700/60"}`}>
        {running ? "❚❚ PAUSE" : "▶ RUN"}
      </button>
      <a data-testid="viv-growlab-link" href="/growlab" className={`${btn} bg-stone-950 text-emerald-300 border-emerald-500/40 hover:border-emerald-400 whitespace-nowrap`}>
        GROWLAB 811 →
      </a>
      <span className="ml-auto hidden lg:block text-[9px] font-mono tracking-widest text-stone-500 whitespace-nowrap">
        DRAG TO ORBIT · SCROLL TO ZOOM · TAP A LABEL TO MEET A SPECIES
      </span>
    </footer>
  );
}
